import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react';

const Contact = () => {

  const contactInfo = [
    {
      icon: Phone,
      title: '24x7 Support',
      details: '+91 70175 92307',
      description: 'Call us anytime for immediate assistance'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'support@aquargin.com',
      description: 'Send us your queries and we\'ll respond quickly'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: '24/7 Available',
      description: 'We\'re here whenever you need us'
    },
    {
      icon: MapPin,
      title: 'Global Presence',
      details: 'Serving Worldwide',
      description: 'Supporting businesses across the globe'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-gray-900">
              Get in <span className="text-gradient">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your business with Aquargin? Contact us today and let's discuss how we can help you build amazing applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center card p-6"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                  <p className="text-primary-600 font-medium mb-2">{info.details}</p>
                  <p className="text-gray-600 text-sm">{info.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center space-y-8"
            >
              <div>
                <h2 className="text-3xl font-heading font-bold text-gray-900 mb-4">
                  Let's Build Something Amazing Together
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                  Whether you're looking to streamline your business operations, create custom applications, 
                  or explore our no-code solutions, we're here to help you every step of the way.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="card p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Book a Free Demo</h3>
                  <p className="text-gray-600 mb-4">
                    See Aquargin in action with a personalized demo tailored to your business needs.
                  </p>
                  <button className="btn-outline flex items-center justify-center space-x-2 w-full">
                    <MessageCircle className="w-4 h-4" />
                    <span>Schedule Demo</span>
                  </button>
                </div>

                <div className="card p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Custom Solutions</h3>
                  <p className="text-gray-600 mb-4">
                    Need something specific? Our team can build custom solutions tailored to your exact requirements.
                  </p>
                  <button className="btn-outline w-full">Get Custom Quote</button>
                </div>

                <div className="card p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Partnership Opportunities</h3>
                  <p className="text-gray-600 mb-4">
                    Interested in becoming a partner? Let's discuss how we can work together.
                  </p>
                  <button className="btn-outline w-full">Explore Partnership</button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding gradient-bg text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold">
              Ready to Get Started?
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Join thousands of businesses already using Aquargin to transform their operations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://app.aquargin.com/register" className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-all duration-300">
                Start Free Trial
              </a>
              <a href="/pricing" className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-6 rounded-lg transition-all duration-300">
                View Pricing
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 